import { ContentfulMetafieldCollection } from '@jbhi-fi/apis/src/contentful/types';
import { RefinementContainer } from '../refinements/RefinementContainer';
import CustomInfiniteHits from './CustomInfiniteHits';
import React, { useEffect, useRef, useState } from 'react';
import { createRoutingURL, searchStateToUrl, urlToSearchState } from './searchStateUrlStorageHelpers';
import { Configure, InstantSearch } from 'react-instantsearch';
import { history } from 'instantsearch.js/es/lib/routers';

import { COLLECTION_DEBOUNCE_TIME } from './CollectionContainer';
import { baseConfig } from '../../../config/algolia';
import algoliasearch from 'algoliasearch';
import { buildSearchFilters } from '@jbhi-fi/utils/src/buildAlgoliaSearchFilters/buildAlgoliaSearchFilters';
import { MarketplaceSeller } from '@jbhi-fi/components/src/Marketplace/factories/marketplaceSellerFactory';
import { ProductClient } from '@jbhi-fi/apis/src/algolia/ProductClient';
import { getAnalyticsTags } from '../../../utils/algolia/search-insights';

const { app_id = '', product_families_api_key = '', product_families_index = '', base_facets = [] } = baseConfig;
const facets = [...base_facets, { facet: 'Category', type: 'refinementList' }];
const searchClient = algoliasearch(app_id, product_families_api_key, ProductClient.getAlogliaCache(app_id));
const indexName = product_families_index;

const buildSellerCollectionAlgoliaFilters = (
  collection: ContentfulMetafieldCollection,
  seller: MarketplaceSeller
): string => {
  const sellerCollectionFilters = `isMarketplace = 1 AND (product.seller.id = ${seller.sellerId})`;

  return buildSearchFilters(
    `${sellerCollectionFilters}` + (collection.searchFilters ? ` AND (${collection.searchFilters})` : '')
  );
};

export type SellerCollectionProductsContainerProps = {
  collection: ContentfulMetafieldCollection;
  seller: MarketplaceSeller;
};

export const SellerCollectionProductsContainer = ({ collection, seller }: SellerCollectionProductsContainerProps) => {
  const [searchState, setSearchState] = useState(urlToSearchState(window.location, collection));
  const saveSearchStateUrlToTimoutRef = useRef<number | undefined>(undefined);

  const onSearchStateChange = (updatedSearchStates: any) => {
    window.clearTimeout(saveSearchStateUrlToTimoutRef.current);
    const updatedSearchState = updatedSearchStates[indexName];

    const newQueryState = {
      refinementList: updatedSearchState.refinementList,
      range: updatedSearchState.range,
      hitsPerPage: updatedSearchState.hitsPerPage,
      sortBy: updatedSearchState.sortBy,
      toggle: updatedSearchState.toggle,
    };

    saveSearchStateUrlToTimoutRef.current = window.setTimeout(() => {
      window.history.pushState(updatedSearchState, '', searchStateToUrl(window.location, newQueryState));
    }, COLLECTION_DEBOUNCE_TIME);

    setSearchState(updatedSearchState);
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(saveSearchStateUrlToTimoutRef.current);
    };
  }, []);

  return (
    <>
      <InstantSearch
        searchClient={searchClient}
        indexName={indexName}
        initialUiState={{
          [indexName]: searchState,
        }}
        onStateChange={({ uiState, setUiState }) => {
          onSearchStateChange(uiState);
          setUiState(uiState);
        }}
        future={{
          persistHierarchicalRootCount: false,
          preserveSharedStateOnUnmount: true,
        }}
        routing={{
          router: history({
            cleanUrlOnDispose: false,
            createURL({ routeState }) {
              return createRoutingURL(routeState);
            },
          }),
        }}
      >
        <Configure
          clickAnalytics
          filters={buildSellerCollectionAlgoliaFilters(collection, seller)}
          query={collection.query}
          distinct
          analyticsTags={getAnalyticsTags('discovery')}
        />
        <RefinementContainer
          facets={
            collection.facets && Array.isArray(collection.facets) && collection.facets.length
              ? collection.facets
              : facets
          }
          collection={collection}
          indexName={indexName}
          nbSaleItems={0}
          saleItems={undefined}
          priceRange={undefined}
          isSearchPage={false}
        />
        <CustomInfiniteHits collection={collection} shouldSplitTiles={true} />
      </InstantSearch>
    </>
  );
};
